import React, { useEffect, useState } from "react";
import IconAndTextButton from "../../Atoms/Buttons/IconAndTextButton";
import CustomTextField from "../../Atoms/TextFields/CustomTextField";
import CustomDropdown from "../../Atoms/DropDowns/CustomDropdown";
import plusIcon from "../../../assets/u_plus.svg";
import upload from "../../../assets/upload.svg";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../../hooks/useDebounce";
import { useSolution } from "../../../contexts/SolutionsContext";
import "../css/dashboard.solution.header.css";
import { Chip, CircularProgress } from "@mui/material";
import { useDashboard } from "../../../contexts/DashboardContext";
import usePermissions from "../../../hooks/userPermissions";
import { Color } from "antd/es/color-picker";

const SolutionHeader = ({
  setSearchKey,
  setStatusVal,
  setProviderFilterVal,
  setIndustryFilterVal,
  setSectorFilterVal,
  setFunctionFilterVal = () => { },
}) => {
  const {
    fetchSolutionBFs,
    isSilentLoading,
    fetchSolutionIndustries,
    fetchSolutionSectors,
    fetchSolutionStatus,
    lookUpValues,
    fetchSolutionProviders,
    isLoadingDropdownFields,
    fetchSolutionSectorsSilently,
    isFetchingSectorsSilently
  } = useSolution();

  const { canSolutionCreate } = usePermissions();

  const { changeUploadDrawerState } = useDashboard();

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState();
  const [providerFilter, setProviderFilter] = useState();
  const [industryFilter, setIndustryFilter] = useState();
  const [sectorFilter, setSectorFilter] = useState();

  const [selectedCategory, setSelectedCategory] = useState([]);
  const [sectorValues, setSectorValues] = useState([])

  const navigate = useNavigate();
  const debouncedText = useDebounce(searchText);

  // Fetch categories when component mounts for solution type
  useEffect(() => {
    const fetchDropdownValues = async () => {
      await Promise.allSettled([
        fetchSolutionBFs(),
        fetchSolutionIndustries(),
        fetchSolutionProviders(),
        fetchSolutionSectors(),
        fetchSolutionStatus(),
      ]);
    };

    fetchDropdownValues();
  }, [fetchSolutionBFs]);

  const handleSearchKeyChange = (e) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    if (debouncedText !== undefined) {
      setSearchKey(debouncedText);
    }
  }, [debouncedText]);

  const handleFilterChange = (value) => {
    setStatusVal(value);
    setStatusFilter(value);
  };

  const handleFilterProviderChange = (value) => {
    setProviderFilterVal(value);
    setProviderFilter(value);
  };

  const handleFilterIndustryChange = async (value) => {
    setSectorValues([])
    setIndustryFilterVal(value);
    setIndustryFilter(value);

    const sectorsFromApi = await fetchSolutionSectorsSilently(value)
    if (sectorsFromApi) {
      setSectorValues(sectorsFromApi)
    }
  };

  const handleFilterSectorChange = (value) => {
    setSectorFilterVal(value);
    setSectorFilter(value);
  };

  const handleChipClick = (chipValue) => {
    setSelectedCategory((prev) => {
      let newSelection;

      if (chipValue === "All") {
        newSelection = ["All"];
      } else {
        const isAlreadySelected = prev.includes(chipValue);
        const filteredPrev = prev.filter((item) => item !== "All");
        newSelection = isAlreadySelected
          ? filteredPrev.filter((item) => item !== chipValue)
          : [...filteredPrev, chipValue];
      }

      const finalFilterVal = newSelection.includes("All") ? [] : newSelection;
      setFunctionFilterVal(finalFilterVal);
      return newSelection;
    });
  };

  const handleClearCategories = () => {
    setSelectedCategory([]);
    setFunctionFilterVal([]);
  };

  useEffect(() => {
    setFunctionFilterVal([]);
  }, [setFunctionFilterVal]);

  return (
    <article className="dashboard-sol-header">
      <div className="wd-100" style={{ padding: "32px 48px 0 48px" }}>
        <article className=" wd-100 d-flex justify-content-between align-items-center mb-4">
          <section className="wd-60 d-flex gap-3 align-items-center">
            <div className="wd-48">
              <CustomTextField
                clsNames={"custom-text-field-container"}
                placeholder={"Search"}
                value={searchText}
                handleOnChange={(e) => {
                  handleSearchKeyChange(e);
                }}
              />
            </div>
            <div
              className="dropdown-sol-header-css"
              style={{ minWidth: "14.5%", width: "auto" }}
            >
              <CustomDropdown
                selectValues={[
                  { value: "All", label: "All" },
                  ...(lookUpValues?.industries || []).map((industry) => ({
                    value: industry,
                    label: industry,
                  })),
                ]}
                value={industryFilter}
                name="industry"
                handleOnChange={(val) => {
                  handleFilterIndustryChange(val);
                }}
                dropdownLabel={"Industry"}
                clsNames={"mas-cust-dd-primary mas-cust-dd-2"}
                disabled={isLoadingDropdownFields?.industries}
              />
            </div>
            <div className="dropdown-sol-header-css">
              <CustomDropdown
                selectValues={[
                  { value: "All", label: "All" },
                  ...(sectorValues || []).map((sector) => ({
                    value: sector,
                    label: sector,
                  })),
                ]}
                value={sectorFilter}
                name="sector"
                handleOnChange={(val) => {
                  handleFilterSectorChange(val);
                }}
                dropdownLabel={"Sector"}
                clsNames={"mas-cust-dd-primary mas-cust-dd-2"}
                disabled={isLoadingDropdownFields?.sectors || isFetchingSectorsSilently || sectorValues.length === 0}
              />
            </div>
            <div className="dropdown-sol-header-css">
              <CustomDropdown
                selectValues={[
                  { value: "All", label: "All" },
                  ...(lookUpValues?.status || []).map((status) => ({
                    value: status,
                    label: status,
                  })),
                ]}
                value={statusFilter}
                name="status"
                handleOnChange={(val) => {
                  handleFilterChange(val);
                }}
                dropdownLabel={"Status"}
                clsNames={"mas-cust-dd-primary mas-cust-dd-2"}
                disabled={isLoadingDropdownFields?.status}
              />
            </div>
            <div className="dropdown-sol-header-css">
              <div className="d-flex align-items-center">
                <CustomDropdown
                  selectValues={[
                    { value: "All", label: "All" },
                    ...(lookUpValues?.providers || []).map((provider) => ({
                      value: provider,
                      label: provider,
                    })),
                  ]}
                  value={providerFilter}
                  handleOnChange={(val) => {
                    handleFilterProviderChange(val);
                  }}
                  dropdownLabel={"Provider"}
                  clsNames={"mas-cust-dd-primary mas-cust-dd-2"}
                  disabled={isLoadingDropdownFields?.providers}
                />
                {(isLoadingDropdownFields?.providers ||
                  isLoadingDropdownFields?.industries ||
                  isLoadingDropdownFields?.sectors ||
                  isLoadingDropdownFields?.status ||
                  isFetchingSectorsSilently) && (
                    <CircularProgress
                      className="ms-2"
                      style={{ width: "15px", height: "15px", opacity: 0.8 }}
                    />
                  )}
              </div>
            </div>
          </section>

          {canSolutionCreate && (
            <section className="d-flex gap-3 align-items-center">
              {isSilentLoading && (
                <div className="d-flex align-items-center justify-content-center">
                  <CircularProgress
                    style={{ width: "15px", height: "15px", opacity: 0.8 }}
                  />
                  <div className="sml-font ms-2">Fetching...</div>
                </div>
              )}
              <div>
                {/* <IconAndTextButton
                icon={<img src={upload} alt="Upload" />}
                text="Upload"
                iconPos="left"
                iconStyles={{ width: 20, height: 20, marginRight: 4 }}
                clsNames="mas-secondary-btn-3 mas-btn-lg"
                onClickFn={() => {
                  changeUploadDrawerState(true);
                }}
              /> */}
              </div>
              <div>
                <IconAndTextButton
                  icon={<img src={plusIcon} alt="Register Solution" />}
                  text="Register Solution"
                  iconPos="left"
                  iconStyles={{ width: 20, height: 20, marginRight: 4 }}
                  clsNames="mas-plus-icon-btn-3"
                  onClickFn={() => {
                    navigate("register-solution");
                  }}
                />
              </div>
            </section>
          )}
        </article>

        <article>
          <div className="d-flex align-items-center">
            {["All", ...lookUpValues?.bFs]?.map((item, index) => (
              <Chip
                clickable
                key={index}
                label={item}
                onClick={() => handleChipClick(item)}
                className="categoryChipClass"
                sx={
                  selectedCategory.includes(item)
                    ? { backgroundColor: "#A0DCFF99" }
                    : { backgroundColor: "#FFFFFF" }
                }
              />
            ))}
            {isLoadingDropdownFields?.bFs && (
              <div className="d-flex align-items-center justify-content-center">
                <CircularProgress
                  style={{ width: "15px", height: "15px", opacity: 0.8 }}
                />
                <div className="sml-font ms-2">Loading...</div>
              </div>
            )}
            {lookUpValues?.bFs.length > 0 && selectedCategory.length > 0 && (
              <Chip
                label="Reset all"
                //   color="primary"
                clickable
                onClick={handleClearCategories}
                className="categoryChipClass"
                id="clearAllChip"
              />
            )}
          </div>
        </article>
      </div>
    </article>
  );
};

export default SolutionHeader;
